import { Button } from "@/components/core/buttons/button";
import { useModal } from "@/components/core/modals/use-modal";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { IconDragDrop, IconPlus, IconTrash } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { memo, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";

type AcceptedFile = {
	file_id: string; // Unique identifier for drag-and-drop
	file: File;
	preview: string;
	name: string;
	size: number;
	type: string;
	date: string;
};

export default function DragDropFile({
	className,
	onSubmit,
	onDelete,
	onClear,
	isCaseFiling,
	initialFiles,
}: {
	className?: string;
	onSubmit: (files: AcceptedFile[]) => void;
	onClear: (files: AcceptedFile[]) => void;
	onDelete: (file: AcceptedFile) => void;
	isCaseFiling?: boolean;
	initialFiles?: any;
}) {
	const modal = useModal();

	const { acceptedFiles, getRootProps, getInputProps, open, isDragActive } = useDropzone({
		accept: {
			"application/pdf": [],
			"image/*": [],
			"audio/*": [],
			"application/vnd.ms-excel": [],
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
			"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
		},
		noClick: true,
		noKeyboard: true,
		onDrop: (acceptedFiles) => {
			setFile((prevFiles) => {
				const newFiles = acceptedFiles.map((file) => ({
					file_id: `file-${Date.now()}-${Math.random()}`, // Unique ID for drag-and-drop
					preview: URL.createObjectURL(file),
					file,
					name: file.name,
					size: file.size,
					type: file.type,
					date: new Date(file.lastModified).toLocaleString("en-US"),
				}));

				const fileNames = new Set(prevFiles.map((f) => f.name));
				const uniqueNewFiles = newFiles.filter((f) => !fileNames.has(f.name));

				return [...prevFiles, ...uniqueNewFiles];
			});
		},
	});

	const [files, setFile] = useState<AcceptedFile[]>([]);

	useEffect(() => {
		if (initialFiles) {
			setFile(initialFiles);
		}
	}, [initialFiles]);

	useEffect(() => {
		if (acceptedFiles.length > 0) {
			setFile((prevFiles) => {
				const newFiles = acceptedFiles.map((file) => ({
					preview: URL.createObjectURL(file),
					file,
					name: file.name,
					size: file.size,
					type: file.type,

					date: new Date(file.lastModified).toLocaleString("en-US", {
						year: "numeric",
						month: "2-digit",
						day: "2-digit",
						hour: "2-digit",
						minute: "2-digit",
						second: "2-digit",
					}),
				}));

				const fileNames = new Set(prevFiles.map((f) => f.name));
				const uniqueNewFiles = newFiles.filter((f) => !fileNames.has(f.name));
				return [...prevFiles, ...uniqueNewFiles];
			});
		}
	}, [acceptedFiles]);

	const handleDelete = (index: number) => {
		setFile((prevFiles) => {
			const removedFile = prevFiles[index];

			// Always call onDelete when a file is removed
			onDelete?.(removedFile);

			return prevFiles.filter((_, i) => i !== index);
		});
	};

	const handleTrash = (index: number) => {
		const modalId = modal.openModal({
			type: "confirm",
			variant: "red",
			title: "Are you sure?",
			description: "Do you really want to remove this file?",
			onConfirm: async () => {
				handleDelete(index);
				modal.closeModal(modalId);
			},
		});
	};

	const handleFileUpload = () => {
		if (files.length > 0) {
			onSubmit(files);
		}
		setFile([]);
	};

	const handleClearList = () => {
		const modalId = modal.openModal({
			type: "confirm",
			variant: "red",
			title: "Are you sure?",
			description: "Do you really want to remove all files?",
			onConfirm: async () => {
				if (files.length > 0) {
					onClear(files);
				}
				setFile([]);
				modal.closeModal(modalId);
			},
		});
	};

	const handleDragEnd = (event: any) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;

		setFile((prev) => {
			const oldIndex = prev.findIndex((f) => f.file_id === active.id);
			const newIndex = prev.findIndex((f) => f.file_id === over.id);
			return arrayMove(prev, oldIndex, newIndex);
		});
	};

	useEffect(() => {
		setFile((prevFiles) =>
			prevFiles.map((file, index) => ({
				...file,
				file_id: file.file_id || `file-${index}-${Date.now()}`,
			})),
		);
	}, []);

	isCaseFiling &&
		useEffect(() => {
			if (files.length > 0) {
				onSubmit(files);
			}
		}, [onSubmit, files]);

	const DraggableRow = memo(function DraggableRow({
		file,
		onDelete,
	}: {
		file: AcceptedFile;
		index: number;
		onDelete: () => void;
	}) {
		const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: file.file_id });

		const style = {
			transform: CSS.Transform.toString(transform),
			transition,
		};

		return (
			<tr ref={setNodeRef} className="border-b border-border/50 last:border-b-0 bg-background" style={style}>
				<td className="w-[5%] py-2 px-3 text-left text-sm text-foreground-dark">
					<div className="inline-flex py-2 px-3 text-foreground/50 hover:text-foreground-dark" {...attributes} {...listeners}>
						<IconDragDrop size={18} stroke={2} />
					</div>
				</td>
				<td className="w-[45%] py-2 px-3 text-left text-sm text-foreground-dark">
					<Link to={file.preview} target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600 hover:underline">
						{file.name}
					</Link>
				</td>
				<td className="w-[20%] py-2 px-3 text-left text-sm text-foreground-dark">{(file.size / 1024).toFixed(2)} KB</td>
				<td className="w-[20%] py-2 px-3 text-left text-sm text-foreground-dark">{file.type}</td>
				<td className="w-[10%] py-2 px-3 text-center text-sm text-foreground-dark">
					<button type="button" className="text-red-500 hover:text-red-700" onClick={() => onDelete()}>
						<IconTrash size={16} stroke={2} />
					</button>
				</td>
			</tr>
		);
	});

	return (
		<div className="relative flex flex-col w-full gap-2">
			<div className="flex flex-col w-full">
				{files.length !== 0 ? (
					<div
						{...getRootProps({
							className: "dropzone inline-flex flex-col w-full gap-2",
						})}
					>
						<div className="flex flex-col w-full">
							<span className="font-bold text-sm text-foreground-dark">Added ({files.length}) file/s</span>
							<span className="relative inline-flex text-sm text-foreground/70">Drag 'n' drop files on table to add more.</span>
						</div>

						<div className="flex flex-col w-full rounded-md border border-border/50 overflow-hidden">
							<DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
								<SortableContext items={files.map((file) => file.file_id)}>
									<table className="w-full">
										<thead>
											<tr className="border-b border-border/50">
												<th className="w-[1%] py-2 px-3 text-left text-sm text-foreground-dark" />
												<th className="w-90 py-2 px-3 text-left text-sm text-foreground-dark">File Name</th>
												<th className="py-2 px-3 text-left text-sm text-foreground-dark">Size</th>
												<th className="py-2 px-3 text-left text-sm text-foreground-dark">Type</th>
												<th className="py-2 px-3 text-left text-sm text-foreground-dark">Action</th>
											</tr>
										</thead>
										<tbody>
											{files.map((file, index) => (
												<DraggableRow key={file.file_id} file={file} index={index} onDelete={() => handleTrash(index)} />
											))}
										</tbody>
									</table>
								</SortableContext>
							</DndContext>
						</div>
					</div>
				) : (
					<div
						{...getRootProps({
							className: "dropzone inline-flex flex-col justify-center items-center w-full min-h-[240px]",
						})}
					>
						<input {...getInputProps()} />
						<div className="inline-flex flex-col justify-center items-center h-full w-auto gap-2">
							<div className="inline-flex justify-center items-center self-center w-[100px] h-[100px] p-2 rounded-full border border-border/30">
								<div className="inline-flex justify-center items-center self-center w-full h-full p-2 rounded-full border border-border/50">
									<div className="inline-flex justify-center items-center self-center w-full h-full p-2 rounded-full border border-border/70">
										<div className="inline-flex justify-center items-center self-center w-full h-full rounded-full bg-border/50 text-foreground">
											<IconDragDrop size={24} stroke={1.5} />
										</div>
									</div>
								</div>
							</div>
							<p className="max-w-[320px] text-center text-base text-foreground/50">
								Drag 'n' drop some files here, or click the button below to add manually.
							</p>
							<Button className="mt-2" type="button" onClick={open} size="sm">
								<IconPlus size={14} stroke={2} />
								<span className="">Add File</span>
							</Button>
						</div>
					</div>
				)}
			</div>

			{files.length > 0 && (
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<Button size="sm" type="button" variant="danger" onClick={handleClearList}>
						Clear List
					</Button>

					{!isCaseFiling && (
						<Button size="sm" type="button" onClick={handleFileUpload}>
							Save
						</Button>
					)}
				</div>
			)}
		</div>
	);
}
